import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // Initialize from localStorage
    return localStorage.getItem('freelancehub_token');
  });
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async (authToken) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(response.data);
      return true;
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('freelancehub_token');
      setToken(null);
      setUser(null);
      return false;
    }
  }, []);

  // Initial load - check for existing token
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('freelancehub_token');
      if (storedToken) {
        setToken(storedToken);
        await fetchUser(storedToken);
      }
      setLoading(false);
    };
    initAuth();
  }, [fetchUser]);

  // Listen for storage changes (cross-tab sync)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'freelancehub_token') {
        if (e.newValue) {
          setToken(e.newValue);
          fetchUser(e.newValue);
        } else {
          setToken(null);
          setUser(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token } = response.data;
    localStorage.setItem('freelancehub_token', access_token);
    setToken(access_token);

    // Fetch user data
    const userResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setUser(userResponse.data);
    return userResponse.data;
  };

  const register = async (name, email, password, membershipType = 'normal') => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, membership_type: membershipType });
    return response.data; // { message, requires_verification: true }
  };

  const verifyCode = async (email, code) => {
    const response = await axios.post(`${API}/auth/verify-code`, { email, code });
    const { access_token } = response.data;
    localStorage.setItem('freelancehub_token', access_token);
    setToken(access_token);
    const userResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setUser(userResponse.data);
    return userResponse.data;
  };

  const resendCode = async (email) => {
    await axios.post(`${API}/auth/resend-code`, { email });
  };

  const logout = () => {
    localStorage.removeItem('freelancehub_token');
    setToken(null);
    setUser(null);
  };

  // Refresh auth state (useful for manual refresh)
  const refreshAuth = async () => {
    const storedToken = localStorage.getItem('freelancehub_token');
    if (storedToken) {
      setToken(storedToken);
      return await fetchUser(storedToken);
    }
    return false;
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, verifyCode, resendCode, logout, loading, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
